/* ======================================================================
   Bundle Cart Display Override
   Shows tiered bundle pricing in cart instead of base Shopify prices
   ====================================================================== */

(function() {
  'use strict';

  // Format money helper
  function formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  // Recalculate price based on current quantity (matches bundle builder logic)
  function getTieredPrice(quantity, isSubscription) {
    if (isSubscription) {
      return 1170; // $11.70 in cents
    }

    if (quantity <= 4) {
      return 1300; // $13/jar
    } else if (quantity === 5) {
      return 1300; // $13/jar
    } else {
      return 1200; // $12/jar for 6+
    }
  }

  // Update cart display to show bundle prices
  function updateBundleDisplay() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Find bundle items (items with _bundle_id property)
        const bundleItems = cart.items.filter(item =>
          item.properties && item.properties._bundle_id
        );

        if (bundleItems.length === 0) return;

        // Group items by bundle ID and calculate totals per bundle
        const bundles = {};
        bundleItems.forEach(item => {
          const bundleId = item.properties._bundle_id;
          if (!bundles[bundleId]) {
            bundles[bundleId] = {
              items: [],
              totalQuantity: 0,
              isSubscription: item.properties._is_subscription === 'yes'
            };
          }
          bundles[bundleId].items.push(item);
          bundles[bundleId].totalQuantity += item.quantity;
        });

        // Calculate total across all bundles with recalculated pricing
        let calculatedTotal = 0;
        let originalTotal = 0;

        Object.values(bundles).forEach(bundle => {
          // Recalculate price per jar based on CURRENT total quantity
          const pricePerJar = getTieredPrice(bundle.totalQuantity, bundle.isSubscription);

          bundle.items.forEach(item => {
            calculatedTotal += pricePerJar * item.quantity;
            originalTotal += (item.original_price || item.price) * item.quantity;
          });
        });

        // Check if any bundle is a subscription
        const isSubscription = bundleItems.some(item =>
          item.properties._is_subscription === 'yes'
        );

        console.log('Bundle cart display:', {
          calculatedTotal,
          originalTotal,
          isSubscription,
          itemCount: bundleItems.length,
          bundles: Object.keys(bundles).length
        });

        // Update each bundle item display with recalculated price
        Object.values(bundles).forEach(bundle => {
          const pricePerJar = getTieredPrice(bundle.totalQuantity, bundle.isSubscription);
          bundle.items.forEach(item => {
            updateCartItem(item, pricePerJar);
          });
        });

        // Update cart subtotal if all items are bundle items
        if (bundleItems.length === cart.items.length) {
          updateCartSubtotal(calculatedTotal, isSubscription, bundleItems);
        }

        // Add bundle messaging (use recalculated price for first bundle)
        const firstBundle = Object.values(bundles)[0];
        const firstBundlePrice = getTieredPrice(firstBundle.totalQuantity, firstBundle.isSubscription);
        addBundleMessaging(bundleItems.length, firstBundlePrice, isSubscription);
      })
      .catch(error => {
        console.error('Error updating bundle display:', error);
      });
  }

  // Update individual cart item display
  function updateCartItem(item, displayPrice) {
    // Try multiple selectors for different theme structures
    const selectors = [
      `[data-id="${item.key}"]`,  // For themes using data-id
      `.cart-form-item[data-line="${item.index + 1}"]`,  // For themes using cart-form-item (1-indexed)
      `[data-cart-item-key="${item.key}"]`,
      `[data-key="${item.key}"]`,
      `.cart-item[data-index="${item.index}"]`,
      `.cart__row[data-line-index="${item.index}"]`
    ];

    let itemElement = null;
    for (const selector of selectors) {
      itemElement = document.querySelector(selector);
      if (itemElement) break;
    }

    if (!itemElement) {
      console.warn('Could not find cart item element for:', item.key);
      return;
    }

    // Get original price - for subscriptions, use base price (1300) instead of discounted price
    const isSubscription = item.properties && item.properties._is_subscription === 'yes';
    const originalPrice = isSubscription ? 1300 : (item.original_price || item.price);
    const showStrikethrough = originalPrice > displayPrice;

    // Update price display (try multiple selectors)
    const priceSelectors = [
      '.cart-item__price',
      '.cart__price',
      '[data-cart-item-price]',
      '.price'
    ];

    for (const selector of priceSelectors) {
      const priceElement = itemElement.querySelector(selector);
      if (priceElement) {
        if (showStrikethrough) {
          priceElement.innerHTML = `<span style="text-decoration: line-through; color: #999; margin-right: 8px;">${formatMoney(originalPrice)}</span>${formatMoney(displayPrice)}`;
        } else {
          priceElement.textContent = formatMoney(displayPrice);
        }
        break;
      }
    }

    // Update line total
    const lineTotalSelectors = [
      '.cart-item__total',
      '.cart-item__line-price',
      '.cart__final-price',
      '[data-cart-item-line-price]'
    ];

    for (const selector of lineTotalSelectors) {
      const lineTotalElement = itemElement.querySelector(selector);
      if (lineTotalElement) {
        if (showStrikethrough) {
          const originalLineTotal = originalPrice * item.quantity;
          const bundleLineTotal = displayPrice * item.quantity;
          lineTotalElement.innerHTML = `<span style="text-decoration: line-through; color: #999; margin-right: 8px;">${formatMoney(originalLineTotal)}</span>${formatMoney(bundleLineTotal)}`;
        } else {
          lineTotalElement.textContent = formatMoney(displayPrice * item.quantity);
        }
        break;
      }
    }

    // Add bundle badge if not already present
    if (!itemElement.querySelector('.tlh-bundle-badge')) {
      const badge = document.createElement('span');
      badge.className = 'tlh-bundle-badge';
      badge.textContent = 'Bundle Item';
      badge.style.cssText = `
        display: inline-block;
        background: #000;
        color: #fff;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        margin: 4px 0;
      `;

      // Try to insert badge near product title
      const titleSelectors = [
        '.cart-item__details',
        '.cart-item__name',
        '.cart__product-title',
        '.product-title'
      ];

      for (const selector of titleSelectors) {
        const titleElement = itemElement.querySelector(selector);
        if (titleElement) {
          titleElement.insertAdjacentElement('afterbegin', badge);
          break;
        }
      }
    }
  }

  // Update cart subtotal display
  function updateCartSubtotal(displayTotal, isSubscription, bundleItems) {
    const subtotalSelectors = [
      '.cart__total strong',
      '.cart__total .text-size--heading',
      '.cart-subtotal__price',
      '.cart__subtotal strong',
      '[data-cart-subtotal]',
      '.totals__subtotal-value'
    ];

    for (const selector of subtotalSelectors) {
      const subtotalElement = document.querySelector(selector);
      if (subtotalElement) {
        // Calculate original total - for subscriptions, use base price (1300) instead of discounted price
        let originalTotal = 0;
        bundleItems.forEach(item => {
          const itemIsSubscription = item.properties && item.properties._is_subscription === 'yes';
          const originalPrice = itemIsSubscription ? 1300 : (item.original_price || item.price);
          originalTotal += originalPrice * item.quantity;
        });

        // Show strikethrough if there's a discount
        if (originalTotal > displayTotal) {
          subtotalElement.innerHTML = `<span style="text-decoration: line-through; color: #999; margin-right: 8px;">${formatMoney(originalTotal)}</span>${formatMoney(displayTotal)}`;
        } else {
          subtotalElement.textContent = formatMoney(displayTotal);
        }

        // Add note about checkout discount if subscription
        if (isSubscription && !document.querySelector('.tlh-subscription-note')) {
          const note = document.createElement('div');
          note.className = 'tlh-subscription-note';
          note.innerHTML = '💡 Subscription discount (10%) applied';
          note.style.cssText = `
            background: #fffbe9;
            border-left: 4px solid #f4a300;
            padding: 12px;
            margin-top: 12px;
            font-size: 14px;
            color: #222;
            border-radius: 4px;
          `;
          subtotalElement.parentElement.insertAdjacentElement('afterend', note);
        }
        break;
      }
    }
  }

  // Add bundle messaging
  function addBundleMessaging(itemCount, pricePerJar, isSubscription) {
    // Don't add if already exists
    if (document.querySelector('.tlh-bundle-pricing-info')) return;

    const cartTotalsSelectors = [
      '.cart__footer',
      '.cart-footer',
      '.totals',
      '.cart__bottom'
    ];

    let cartFooter = null;
    for (const selector of cartTotalsSelectors) {
      cartFooter = document.querySelector(selector);
      if (cartFooter) break;
    }

    if (!cartFooter) return;

    // Create pricing info message
    const message = document.createElement('div');
    message.className = 'tlh-bundle-pricing-info';

    let messageText = '';
    const priceDisplay = formatMoney(pricePerJar);

    if (isSubscription) {
      messageText = `✓ <strong>Subscription Bundle</strong> - ${itemCount} jars at ${priceDisplay} each (10% off)`;
    } else if (itemCount >= 6) {
      messageText = `✓ <strong>Bundle Discount</strong> - ${itemCount} jars at ${priceDisplay} each ($1 off per jar)`;
    } else if (itemCount === 5) {
      messageText = `✓ <strong>${itemCount} jars</strong> at ${priceDisplay} each + FREE shipping`;
    } else {
      messageText = `${itemCount} jar${itemCount > 1 ? 's' : ''} at ${priceDisplay} each`;
    }

    message.innerHTML = messageText;
    message.style.cssText = `
      background: #f0f9ff;
      border-left: 4px solid #0ea5e9;
      padding: 12px;
      margin: 12px 0;
      font-size: 14px;
      color: #222;
      border-radius: 4px;
    `;

    // Add discount applies at checkout note for 6+ jars
    if (itemCount >= 6 && !isSubscription) {
      const checkoutNote = document.createElement('div');
      checkoutNote.className = 'tlh-checkout-discount-note';
      checkoutNote.innerHTML = '💡 Bundle discount applies automatically at checkout';
      checkoutNote.style.cssText = `
        background: #fffbe9;
        border-left: 4px solid #f4a300;
        padding: 12px;
        margin-top: 8px;
        font-size: 13px;
        color: #666;
        border-radius: 4px;
      `;
      message.appendChild(checkoutNote);
    }

    cartFooter.insertAdjacentElement('afterbegin', message);
  }

  // Debounced wrapper — collapses multiple rapid calls into one
  let debounceTimer = null;
  let isUpdating = false;
  function debouncedUpdate() {
    if (isUpdating) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      isUpdating = true;
      updateBundleDisplay();
      setTimeout(() => { isUpdating = false; }, 1000);
    }, 300);
  }

  // Initialize
  function init() {
    debouncedUpdate();
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Listen for cart updates
  document.addEventListener('cart:updated', debouncedUpdate);
  document.addEventListener('cart-updated', debouncedUpdate);

  // Also listen for custom Shopify cart events
  if (window.Shopify && window.Shopify.onCartUpdate) {
    const originalOnCartUpdate = window.Shopify.onCartUpdate;
    window.Shopify.onCartUpdate = function() {
      originalOnCartUpdate.apply(this, arguments);
      debouncedUpdate();
    };
  }

  // Watch for cart drawer updates (when AjaxCartSubtotal innerHTML changes)
  const observeCartDrawer = () => {
    const cartSubtotal = document.getElementById('AjaxCartSubtotal');
    if (cartSubtotal) {
      const observer = new MutationObserver(() => {
        debouncedUpdate();
      });
      observer.observe(cartSubtotal, { childList: true, subtree: true });
    }
  };

  // Start observing after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartDrawer);
  } else {
    observeCartDrawer();
  }

  // Expose update function globally for themes that need it
  window.updateBundleCartDisplay = debouncedUpdate;
})();
